/* aurora-facets.js — ORIGINAL collection filter + sort engine (replaces Dawn's facets.js).
   No Dawn code. Three custom elements:
     <aurora-facets>        wraps the filter form(s); intercepts input, fetches the filtered
                            collection via Shopify's Section Rendering API, swaps the results
                            grid + the filter list, and syncs the URL (back/forward safe).
     <aurora-price>         two-handle price range (min/max number inputs, clamped).
     <aurora-facet-remove>  an active-filter pill; its <a href> is a pre-built "remove" URL.

   DOM contract (ours — markup in snippets/aurora-facets.liquid + sections/aurora-collection):
     - the section wrapper carries data-aurora-collection with the section id
     - #AuroraFacets   holds the filter list (replaced on every fetch)
     - #AuroraResults  holds the product grid + count + pagination (replaced on every fetch)
     - each filter form has class .aurora-facets__form
*/
(function () {
  var SECTION_ATTR = '[data-aurora-collection]';

  function sectionId() {
    var el = document.querySelector(SECTION_ATTR);
    return el ? el.getAttribute('data-aurora-collection') : null;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ---- the fetch + swap core ------------------------------------------------ */
  var AuroraFacets = {
    inflight: null,

    // params: a query string like "filter.v.option.color=Gold&sort_by=..."
    render: function (params, updateUrl) {
      var id = sectionId();
      if (!id) return;
      var url = window.location.pathname + '?section_id=' + id + (params ? '&' + params : '');

      // mark loading (CSS shows a dim/spinner)
      var results = document.getElementById('AuroraResults');
      if (results) results.classList.add('is-loading');

      // abort a stale request so fast clicks always land on the latest
      if (this.inflight && this.inflight.abort) this.inflight.abort();
      var ctrl = window.AbortController ? new AbortController() : null;
      this.inflight = ctrl;

      fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          AuroraFacets.swap('AuroraFacets', doc);
          AuroraFacets.swap('AuroraResults', doc);
          AuroraFacets.swap('AuroraCount', doc);
          if (results) results.classList.remove('is-loading');
          AuroraFacets.rebind();
          // scroll the grid into view on a real filter change (not on first load)
          var top = document.querySelector(SECTION_ATTR);
          if (updateUrl && top) {
            var y = top.getBoundingClientRect().top + window.pageYOffset - 80;
            if (window.pageYOffset > y) window.scrollTo({ top: y, behavior: 'smooth' });
          }
        })
        .catch(function (e) {
          if (e && e.name === 'AbortError') return;
          if (results) results.classList.remove('is-loading');
          window.location.search = params; // never strand the shopper
        });

      if (updateUrl !== false) {
        history.pushState({ afParams: params || '' }, '', window.location.pathname + (params ? '?' + params : ''));
      }
    },

    // replace the innerHTML of #id from the fetched document
    swap: function (id, doc) {
      var src = doc.getElementById(id), dest = document.getElementById(id);
      if (src && dest) dest.innerHTML = src.innerHTML;
    },

    // build the query string from every filter form's fields (minus empty price)
    paramsFromForms: function () {
      var forms = document.querySelectorAll('.aurora-facets__form');
      var parts = [];
      forms.forEach(function (form) {
        var fd = new FormData(form);
        fd.forEach(function (value, key) {
          if (value === '') return;              // skip blank price min/max
          parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
        });
      });
      return parts.join('&');
    },

    // re-attach listeners after a swap (the DOM inside was replaced)
    rebind: function () {
      document.querySelectorAll('.aurora-facets__form').forEach(function (form) {
        if (form.__afBound) return;
        form.__afBound = true;
        form.addEventListener('input', AuroraFacets.onInput);
        form.addEventListener('submit', function (e) { e.preventDefault(); });
      });
    },

    onInput: debounce(function () {
      AuroraFacets.render(AuroraFacets.paramsFromForms(), true);
    }, 400)
  };
  AuroraFacets.onInput = AuroraFacets.onInput; // keep reference stable for add/removeEventListener

  /* ---- <aurora-facets> element --------------------------------------------- */
  if (!customElements.get('aurora-facets')) {
    customElements.define('aurora-facets', class extends HTMLElement {
      connectedCallback() { AuroraFacets.rebind(); }
    });
  }

  /* ---- <aurora-facet-remove> : an active-filter / clear-all pill ----------- */
  if (!customElements.get('aurora-facet-remove')) {
    customElements.define('aurora-facet-remove', class extends HTMLElement {
      connectedCallback() {
        var link = this.querySelector('a');
        if (!link || this.__b) return;
        this.__b = true;
        var self = this;
        link.addEventListener('click', function (e) {
          e.preventDefault();
          // the href already encodes the collection URL WITHOUT this filter
          var q = self.querySelector('a').getAttribute('href').split('?')[1] || '';
          AuroraFacets.render(q, true);
        });
      }
    });
  }

  /* ---- <aurora-price> : dual-handle slider bound to the two text fields ----
     The range inputs are unnamed, so they never reach the query string. Dragging one
     writes the value into the matching named field; the range's own bubbling `input`
     event is what wakes the debounced fetch, which then reads the fields via FormData.
     At full span both fields are emptied so no price param is sent at all. */
  if (!customElements.get('aurora-price')) {
    customElements.define('aurora-price', class extends HTMLElement {
      connectedCallback() {
        if (this.__b) return; this.__b = true;
        this.min = this.querySelector('[data-af-price-min]');
        this.max = this.querySelector('[data-af-price-max]');
        this.rmin = this.querySelector('[data-af-range-min]');
        this.rmax = this.querySelector('[data-af-range-max]');
        this.fill = this.querySelector('[data-af-fill]');
        this.cap = parseFloat(this.getAttribute('data-af-cap')) || 0;
        var self = this;

        if (this.rmin && this.rmax) {
          [this.rmin, this.rmax].forEach(function (r) {
            r.addEventListener('input', function () { self.fromSlider(); });
          });
        }
        [this.min, this.max].forEach(function (i) {
          if (!i) return;
          // keep it numeric without a type=number spinner
          i.addEventListener('input', function () {
            var cleaned = i.value.replace(/[^\d]/g, '');
            if (cleaned !== i.value) i.value = cleaned;
            self.fromFields();
          });
          i.addEventListener('change', function () { self.fromFields(true); });
        });
        this.paint();
      }

      // clamp so the handles can never cross
      fromSlider() {
        var lo = parseFloat(this.rmin.value), hi = parseFloat(this.rmax.value);
        if (lo > hi) { var t = lo; lo = hi; hi = t; this.rmin.value = lo; this.rmax.value = hi; }
        var full = (lo <= 0 && hi >= this.cap);
        if (this.min) this.min.value = full ? '' : lo;
        if (this.max) this.max.value = full ? '' : hi;
        this.paint();
      }

      // typing in a field moves the handles
      fromFields(commit) {
        if (!this.rmin || !this.rmax) return;
        var lo = parseFloat(this.min && this.min.value);
        var hi = parseFloat(this.max && this.max.value);
        if (isNaN(lo)) lo = 0;
        if (isNaN(hi)) hi = this.cap;
        lo = Math.max(0, Math.min(lo, this.cap));
        hi = Math.max(0, Math.min(hi, this.cap));
        if (commit && lo > hi) { var t = lo; lo = hi; hi = t; if (this.min) this.min.value = lo; if (this.max) this.max.value = hi; }
        this.rmin.value = lo;
        this.rmax.value = hi;
        this.paint();
      }

      paint() {
        if (!this.fill || !this.rmin || !this.rmax || !this.cap) return;
        var lo = parseFloat(this.rmin.value) || 0;
        var hi = parseFloat(this.rmax.value);
        if (isNaN(hi)) hi = this.cap;
        this.fill.style.left = (lo / this.cap * 100) + '%';
        this.fill.style.right = (100 - (hi / this.cap * 100)) + '%';
      }
    });
  }

  /* ---- back / forward buttons ---------------------------------------------- */
  window.addEventListener('popstate', function (e) {
    var params = (e.state && typeof e.state.afParams === 'string')
      ? e.state.afParams
      : (window.location.search.replace(/^\?/, ''));
    AuroraFacets.render(params, false);
  });

  /* ---- sort dropdown outside the filter form (still triggers a fetch) ------ */
  document.addEventListener('change', function (e) {
    var sort = e.target.closest('[data-af-sort]');
    if (!sort) return;
    var field = document.querySelector('.aurora-facets__form [name="sort_by"]');
    if (field) {
      field.value = sort.value;                       // keep it inside the form for future fetches
      AuroraFacets.render(AuroraFacets.paramsFromForms(), true);
    } else {
      AuroraFacets.render('sort_by=' + encodeURIComponent(sort.value), true); // sorting w/o filters
    }
  });

  /* ---- filter drawer (mobile / 'drawer' filter type) -----------------------
     Follows the theme's drawer convention: scrim + panel, close on the X, on the
     scrim, on an outside click, and on Escape. The outside-click listener is bound
     deferred so the very click that opened the drawer doesn't immediately close it. */
  function panelEl() { return document.querySelector('.aurora-facets__panel'); }
  function scrimEl() { return document.querySelector('.aurora-facets__scrim'); }
  function toggleEl() { return document.querySelector('[data-af-drawer-open]'); }

  function onEsc(e) { if (e.key === 'Escape' || e.keyCode === 27) closeDrawer(); }

  function onOutside(e) {
    var p = panelEl();
    if (!p || !p.classList.contains('is-open')) return;
    if (p.contains(e.target)) return;                 // inside the drawer
    if (e.target.closest('[data-af-drawer-open]')) return; // the trigger handles itself
    closeDrawer();
  }

  function openDrawer() {
    var p = panelEl();
    if (!p || p.classList.contains('is-open')) return;
    p.classList.add('is-open');
    var s = scrimEl(); if (s) s.classList.add('is-open');
    document.body.classList.add('aurora-facets-locked');
    var t = toggleEl(); if (t) t.setAttribute('aria-expanded', 'true');
    setTimeout(function () { document.addEventListener('click', onOutside); }, 0);
    document.addEventListener('keydown', onEsc);
    var x = p.querySelector('[data-af-drawer-close]'); if (x) x.focus();
  }

  function closeDrawer() {
    var p = panelEl();
    if (!p || !p.classList.contains('is-open')) return;
    p.classList.remove('is-open');
    var s = scrimEl(); if (s) s.classList.remove('is-open');
    document.body.classList.remove('aurora-facets-locked');
    var t = toggleEl();
    if (t) { t.setAttribute('aria-expanded', 'false'); t.focus(); }
    document.removeEventListener('click', onOutside);
    document.removeEventListener('keydown', onEsc);
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-af-drawer-open]')) { e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-af-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }
    // drawer footer "clear all" — same behaviour as an active-filter pill
    var clear = e.target.closest('[data-af-clear]');
    if (clear) { e.preventDefault(); AuroraFacets.render('', true); }
  });

  /* ---- smooth expand / collapse of a filter group --------------------------
     A <details> hides its content the instant [open] is removed, so a close can't
     animate on its own: keep [open] on, run the 1fr→0fr transition, then drop it. */
  function toggleGroup(det) {
    var body = det.querySelector('.aurora-facets__body');
    if (!body) { det.open = !det.open; return; }
    if (det.__afAnim) return;                       // ignore clicks mid-animation

    if (det.open) {
      det.__afAnim = true;
      det.classList.add('is-collapsing');
      var tid;
      var end = function () {
        if (!det.__afAnim) return;
        det.__afAnim = false;
        det.classList.remove('is-collapsing');
        det.open = false;
        body.removeEventListener('transitionend', onEnd);
        clearTimeout(tid);
      };
      var onEnd = function (ev) { if (ev.target === body) end(); };
      body.addEventListener('transitionend', onEnd);
      tid = setTimeout(end, 500);                   // fallback if transitionend never fires
    } else {
      det.open = true;
      det.classList.add('is-expanding');            // paints at 0fr…
      var expand = function () { det.classList.remove('is-expanding'); }; // …then eases to 1fr
      requestAnimationFrame(function () { requestAnimationFrame(expand); });
      // Fallback: if rAF is throttled (hidden/backgrounded tab) the class would never clear
      // and the group would sit [open] at 0fr — i.e. expanded but showing NOTHING. Whichever
      // fires first wins; removing the class twice is harmless.
      setTimeout(expand, 60);
    }
  }

  document.addEventListener('click', function (e) {
    var sum = e.target.closest('.aurora-facets__summary');
    if (!sum) return;
    var det = sum.parentElement;
    if (!det || det.tagName !== 'DETAILS') return;
    e.preventDefault();
    toggleGroup(det);
  });

  AuroraFacets.rebind();
  window.AuroraFacets = AuroraFacets;
})();
